import type { ValueFormatterParams } from 'ag-grid-community';
import appConstants from '@appConstants';
import { MultiSelectFilter } from '@dataGrid/components/columnFilters';
import { mergeUserConfig } from '@dataGrid/gridUtilities';
import GroupCellRenderer from '../Pickups/GroupPickup/GroupCellRenderer';
import DetailGridConfig from './detailGridConfig';
import type { TrailerMovesDetailGridConfig } from './detailGridConfig';
import type { TrailerMoveColDef } from './types';

const countValueFormatter = (params: ValueFormatterParams) => params.value ?? 0;

const createMoveTypeCountGroup = (headerName: string, fieldPrefix: string): TrailerMoveColDef => ({
  headerName,
  headerClass: 'groupHeaderParentPickup',
  minWidth: appConstants.columnWidth.SPECIAL_INSTRUCTION,
  headerTooltip: headerName,
  marryChildren: true,
  groupId: headerName,
  children: [
    {
      headerClass: 'groupHeaderChildren',
      headerName: 'Unassigned',
      headerTooltip: 'Unassigned',
      field: `${fieldPrefix}Unassigned`,
      minWidth: appConstants.columnWidth.W_120,
      filter: MultiSelectFilter,
      valueFormatter: countValueFormatter,
    },
    {
      headerClass: 'groupHeaderChildren',
      headerName: 'Assigned',
      headerTooltip: 'Assigned',
      field: `${fieldPrefix}Assigned`,
      minWidth: appConstants.columnWidth.W_100,
      filter: MultiSelectFilter,
      valueFormatter: countValueFormatter,
    },
    {
      headerClass: 'groupHeaderChildren',
      headerName: 'Completed',
      headerTooltip: 'Completed',
      field: `${fieldPrefix}Completed`,
      minWidth: appConstants.columnWidth.W_110,
      filter: MultiSelectFilter,
      valueFormatter: countValueFormatter,
    },
    {
      headerClass: 'groupHeaderChildren',
      headerName: 'Total',
      headerTooltip: 'Total',
      field: `${fieldPrefix}Total`,
      minWidth: appConstants.columnWidth.W_85,
      filter: MultiSelectFilter,
      valueFormatter: countValueFormatter,
    },
  ],
});

export interface GridConfigConfiguration {
  userProfile?: {
    columnPref?: Record<string, unknown>;
  };
}

export interface TrailerMovesGridConfig {
  title: string;
  keyColumn: string;
  rowSelection: string;
  rowDeselection: boolean;
  defaultColDef: {
    sortable: boolean;
    filter: boolean;
    resizable: boolean;
    editable: boolean;
    suppressSpanHeaderHeight: boolean;
  };
  detailDefaultColDef: TrailerMovesDetailGridConfig['defaultColDef'];
  detailColumns: TrailerMoveColDef[];
  columns: TrailerMoveColDef[];
}

export default class GridConfig {
  name: string;
  config: TrailerMovesGridConfig;

  constructor(configuration?: GridConfigConfiguration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }

  static getConfiguration({ userProfile }: GridConfigConfiguration = {}): TrailerMovesGridConfig {
    const defaultConfig = GridConfig.getDefaultConfiguration(userProfile);
    const userConfig = userProfile?.columnPref;
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    }) as TrailerMovesGridConfig;
  }

  static getDefaultConfiguration(userProfile?: GridConfigConfiguration['userProfile']): TrailerMovesGridConfig {
    const detailConfig = DetailGridConfig.getDefaultConfiguration(userProfile);
    return {
      title: appConstants.gridNames.TrailerMovesGrid,
      keyColumn: 'rmaxShipperCode',
      rowSelection: 'single',
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        editable: false,
        suppressSpanHeaderHeight: true,
      },
      detailDefaultColDef: detailConfig.defaultColDef,
      detailColumns: detailConfig.columns,
      columns: [
        {
          headerName: '',
          minWidth: appConstants.columnWidth.MERGED_PICKUP_ICON,
          maxWidth: appConstants.columnWidth.MERGED_PICKUP_ICON,
          field: 'isMergedPickup',
          lockPosition: true,
          cellClass: ['gridCell-centerAlign', 'gridCell-merge'],
          filter: false,
          cellRenderer: GroupCellRenderer,
          isInvisibleInShowHide: true,
        },
        {
          headerName: 'Customer',
          headerTooltip: 'Customer',
          field: 'shipperName',
          lockPosition: true,
          lockPinned: true,
          minWidth: appConstants.columnWidth.CONSIGNEE_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
            width: appConstants.columnWidth.W_300,
          },
          cellRenderer: GroupCellRenderer,
          isInvisibleInShowHide: true,
        },
        createMoveTypeCountGroup('Swaps', 'swaps'),
        createMoveTypeCountGroup('Drops', 'drops'),
        createMoveTypeCountGroup('Hooks', 'hooks'),
        {
          headerName: 'Spotted Trailers',
          headerTooltip: 'Spotted Trailers',
          field: 'spottedTrailers',
          minWidth: appConstants.columnWidth.W_160,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: countValueFormatter,
        },
      ],
    };
  }
}
